import { type ApolloError, gql, useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { Transaction } from "@/apis/gql/types";
import { useTenant } from "@/providers/session";

import { GET_ALL_TRANSACTIONS } from "./get-all";

export enum TriggerTransactionBookingsClearingReason {
  DISCOUNT = "discount",
  FXGAIN = "fx-gain",
  FXLOSS = "fx-loss",
  PARTIAL_PAYMENT = "partial-payment",
  MANUAL_ENTRY = "manual-entry",
}

type TriggerTransactionBookingsClearingReasonInput = {
  bankTransactionId: string;
  reason?: TriggerTransactionBookingsClearingReason;
};

export const useTriggerTransactionBookingsClearingReason = (): [
  (
    input: TriggerTransactionBookingsClearingReasonInput,
  ) => Promise<Transaction[]>,
  {
    loading: boolean;
    error?: ApolloError;
  },
] => {
  const { t } = useTranslation();
  const { tenantId } = useTenant();

  const [triggerTransactionBookingsClearingReason, { loading, error }] =
    useMutation<{
      triggerTransactionBookingsClearingReason: Transaction[];
    }>(TRIGGER_TRANSACTION_BOOKINGS_CLEARING_REASON_MUTATION, {
      refetchQueries: [GET_ALL_TRANSACTIONS],
      awaitRefetchQueries: true,
      update: (cache, { data }) => {
        if (!data?.triggerTransactionBookingsClearingReason) return;

        cache.evict({ fieldName: "transactions" });
        cache.gc();
      },
    });

  const handleConfirmTransactionBooking = async (
    input: TriggerTransactionBookingsClearingReasonInput,
  ) => {
    if (!tenantId) {
      throw new Error("Tenant ID is required");
    }

    if (!input.bankTransactionId) {
      throw new Error("Bank transaction ID is required");
    }

    const toastId = toast.loading(t("generic_loading"));

    try {
      const { data: response } = await triggerTransactionBookingsClearingReason(
        {
          variables: {
            bankTransactionId: input.bankTransactionId,
            reason: input.reason,
          },
        },
      );

      if (!response) {
        throw new Error(
          "No response from triggerTransactionBookingsClearingReason",
        );
      }

      toast.dismiss(toastId);

      return response.triggerTransactionBookingsClearingReason;
    } catch (error) {
      toast.error(t("generic_error"), { id: toastId, richColors: true });
      throw error;
    }
  };

  return [handleConfirmTransactionBooking, { loading, error }];
};

export const TRIGGER_TRANSACTION_BOOKINGS_CLEARING_REASON_MUTATION = gql`
  mutation TriggerTransactionBookingsClearingReason(
    $bankTransactionId: String!
    $reason: String
  ) {
    triggerTransactionBookingsClearingReason(
      bankTransactionId: $bankTransactionId
      reason: $reason
    ) {
      bankTxnId
    }
  }
`;
